// ─── buildResultsCallData ────────────────────────────────────────────────
// 2026-07-26 — Module « Préparer l'appel résultats » (cadrage = réponses
// écrites d'Anissa, cf. docs/SPEC-appel-resultats.md).
//
// PUR, sans I/O : transforme journey_state.results_data (+ form) en données
// prêtes pour le panneau privé et pour le prompt IA.
//
// DOCTRINE (non négociable) : la classification vient d'ANISSA, pas de
// l'IA et pas d'ici. « Ce qui ressort » = les éléments qu'elle a elle-même
// marqués eleve / sous_optimal / surveiller à la saisie. Ce module ne
// compare AUCUNE valeur à AUCUNE plage — il organise ses classements.
// Les plages du catalogue sont transportées à titre indicatif PRIVÉ.

import { getMarker } from './clinical/catalog/markers';
import { resolveRiskProfileFlags, findUnknownRiskFlags } from './riskProfileFlags';

// Libellés de thèmes côté appel (mêmes axes que CATEGORIES du parcours).
export const THEME_LABELS = {
  carence: 'Fer & carences',
  inflammation: 'Inflammation',
  metabolique: 'Glycémie & métabolisme',
  hormonal: 'Hormones & thyroïde',
  microbiote: 'Microbiote & digestion',
  autre: 'Autres analyses',
};
// Ordre d'affichage fixe (les thèmes vides sont omis).
export const THEME_ORDER = ['carence', 'inflammation', 'metabolique', 'hormonal', 'microbiote', 'autre'];

// Statuts saisis par Anissa qui « ressortent » pendant l'appel.
const ATTENTION_STATUSES = new Set(['eleve', 'sous_optimal', 'surveiller']);

const STATUS_LABELS = {
  optimal: 'Optimal',
  surveiller: 'À surveiller',
  sous_optimal: 'Sous-optimal',
  eleve: 'Élevé',
  non_interpretable: 'Non interprétable',
};

function normAxis(axis) {
  return THEME_LABELS[axis] ? axis : 'autre';
}

function pushItem(items, { label, value, unit, synthesis, status, axis, refRange }) {
  const cleanLabel = (label || '').trim();
  const cleanValue = (value || '').toString().trim();
  const cleanSynth = (synthesis || '').trim();
  // Une ligne n'existe pour l'appel que si Anissa a saisi quelque chose.
  if (!cleanLabel || (!cleanValue && !cleanSynth)) return;
  items.push({
    label: cleanLabel,
    value: cleanValue || null,
    unit: (unit || '').trim() || null,
    synthesis: cleanSynth || null,
    status: status || null,
    status_label: STATUS_LABELS[status] || null,
    axis: normAxis(axis),
    // Plage indicative du catalogue — PRIVÉE (jamais envoyée telle quelle
    // à la cliente ; l'IA ne s'en sert pas pour classer).
    ref_range: refRange || null,
  });
}

/** Aplati from_plan + external en items homogènes. */
function collectItems(resultsData) {
  const items = [];
  const fromPlan = Array.isArray(resultsData?.from_plan) ? resultsData.from_plan : [];
  for (const test of fromPlan) {
    if (!test) continue;
    const markers = Array.isArray(test.markers) ? test.markers.filter(Boolean) : [];
    for (const m of markers) {
      const cat = m.marker_code ? getMarker(m.marker_code) : null;
      pushItem(items, {
        label: m.label || cat?.label || m.marker_code,
        value: m.value,
        unit: m.unit || cat?.unit,
        synthesis: m.synthesis,
        status: m.status,
        axis: cat?.axis || test.category,
        refRange: cat?.ref_range || null,
      });
    }
    // Legacy : saisie au niveau du test (avant les markers structurés).
    if (markers.length === 0) {
      pushItem(items, {
        label: test.test_name || test.test_code,
        value: test.value,
        synthesis: test.synthesis,
        status: test.status,
        axis: test.category,
      });
    }
  }
  const external = Array.isArray(resultsData?.external) ? resultsData.external : [];
  for (const ext of external) {
    if (!ext) continue;
    pushItem(items, {
      label: ext.name,
      value: ext.value,
      synthesis: ext.synthesis,
      status: ext.status,
      axis: ext.category,
    });
  }
  return items;
}

/**
 * Garde fail-closed : le panneau IA n'existe que s'il y a au moins une
 * ligne d'analyse exploitable (label + valeur ou synthèse).
 */
export function hasEnoughForResultsCall(resultsData) {
  return collectItems(resultsData).length > 0;
}

/**
 * @param {object} args
 * @param {object} args.journeyState - client.journey_state
 * @param {object} [args.form]       - client.form (flags de présentation)
 * @param {string} [args.prenom]     - prénom cliente (sinon form.prenom)
 * @returns données du panneau : thèmes ordonnés avec ressort/rassurer,
 *          compteurs, synthèse globale d'Anissa, drapeaux d'adaptation.
 */
export function buildResultsCallData({ journeyState, form = null, prenom = null }) {
  const resultsData = journeyState?.results_data || {};
  const items = collectItems(resultsData);

  const byAxis = new Map();
  for (const item of items) {
    if (!byAxis.has(item.axis)) {
      byAxis.set(item.axis, { attention: [], rassurer: [], autres: [] });
    }
    const bucket = byAxis.get(item.axis);
    if (item.status && ATTENTION_STATUSES.has(item.status)) bucket.attention.push(item);
    else if (item.status === 'optimal') bucket.rassurer.push(item);
    else bucket.autres.push(item); // non_interpretable ou sans statut saisi
  }

  const themes = THEME_ORDER
    .filter((axis) => byAxis.has(axis))
    .map((axis) => ({
      axis,
      label: THEME_LABELS[axis],
      ...byAxis.get(axis),
    }));

  const riskFlags = resolveRiskProfileFlags(form);
  const sansChiffres = form?.presentationSansChiffres === true;
  // Revue clinique-guard R5 (2026-07-26) : un flag de risque ILLISIBLE ne
  // doit jamais disparaître en silence → profil traité comme sensible.
  const unknownFlags = findUnknownRiskFlags(form);

  return {
    themes,
    counts: {
      total: items.length,
      attention: items.filter((i) => i.status && ATTENTION_STATUSES.has(i.status)).length,
      optimal: items.filter((i) => i.status === 'optimal').length,
      autres: items.filter((i) => !i.status || i.status === 'non_interpretable').length,
    },
    global_synthesis: (resultsData.global_synthesis || '').trim() || null,
    prenom: (prenom || form?.prenom || '').trim() || null,
    // Adaptation des points d'échange (Q7) : chiffres visibles dans la vue
    // privée, mais points destinés à l'appel sans aucun chiffre si sensible.
    sensitive: sansChiffres || riskFlags.includes('tca') || unknownFlags.length > 0,
    sans_chiffres: sansChiffres,
    risk_flags: riskFlags,
  };
}
